/**
 * Path translation for the nav bar's "New App (Beta)" link, which mirrors the CURRENT route onto
 * the Vue 3 rewrite. Most routes are spelled identically in both apps, so the default is a
 * pass-through; this module holds the routes whose shape diverged, where handing the rewrite our
 * URL verbatim drops the user somewhere other than where they were.
 *
 * Today that is the dashboard alone:
 *   vue2  /dashboard/:view?<filters>       (router.js:77; DashboardPage's `view` prop and its
 *                                           `dashboardParameters` schema, DashboardPage.vue:1314)
 *   vue3  /dashboard?view=:view&<filters>  (foundation/src/router/index.ts — one `/dashboard`
 *                                           record; every filter is read out of the query by
 *                                           useDashboardFilters' `fromQuery`)
 *
 * The two filter vocabularies overlap heavily but not exactly, and — the part that bites — several
 * params have DIFFERENT DEFAULTS, so a key that is absent on one side does not mean what its
 * absence means on the other. Those are translated explicitly below rather than passed through.
 *
 * Counterpart: the rewrite's features/user-settings/lib/legacyAppUrl.ts does this in the opposite
 * direction for its "Legacy App" button. Keep the two in sync — a param added to one mapping needs
 * the other.
 *
 * Lives outside Navigation.vue so it can be unit tested (same reason as projectSearch.js: the
 * component's template uses optional chaining the Vue 2 template compiler cannot parse under jest).
 */

// The views vue3 accepts (useDashboardFilters.ts VALID_VIEWS — the same three names as vue2).
// Anything else, e.g. a stale or hand-typed segment, is dropped rather than forwarded: vue3's
// `parseView` would fall back to the default anyway, and an unknown segment left in the PATH 404s.
const DASHBOARD_VIEWS = ['projects', 'models', 'jobs']

// vue3's default view, omitted from its own URLs (`toQuery` writes `view` only when it isn't this).
const DEFAULT_DASHBOARD_VIEW = 'projects'

// Multi-value filters. vue2 repeats the key (`tags=a&tags=b`); vue3 takes one comma-separated
// value. Same meaning on both sides — empty is "no restriction" — so these are omitted when empty.
// Only the name differs for the assigned-users filter.
const CSV_PARAMS = {
  tags: 'tags',
  materials: 'materials',
  stages: 'stages',
  machines: 'machines',
  assignedUsersViewed: 'assignedUsers',
  extension: 'extension',
}

// Booleans that default to FALSE on both sides, so an absent key means the same thing in each and
// we only ever write the "on" case. vue2 spells it `true`; vue3's canonical form is `1`.
const FLAG_PARAMS = {
  incompleteTodos: 'todo',
  needsSlicing: 'needsSlicing',
  needsPrinting: 'needsPrinting',
  needsCounting: 'needsCounting',
  counted: 'counted',
  assigned: 'assigned',
  sortReverse: 'sortReverse',
}

// Scalars vue3 validates against a fixed vocabulary (VALID_GROUP_BY / VALID_SORT). vue2 uses the
// same value names — the rewrite ported them verbatim to keep saved bookmarks working — so only
// the DEFAULT differs, which `translateDashboardQuery` handles.
const VUE3_GROUP_BY = ['', 'status', 'dueDate', 'dateModified', 'role', 'materials', 'machines', 'tags', 'organization', 'stages', 'color']
const VUE3_SORT = ['', 'dueDate', 'datePutInProduction', 'dateModified', 'dateCreated', 'color']

// Everything not named above is dropped, not forwarded — vue3 ignores keys it doesn't know, so
// carrying them over would only make the mirrored URL look like it said something it doesn't. The
// vue2-only params this loses, all from DashboardPage.vue:1314's schema:
//   view                  - the PATH segment is authoritative here; a query copy would be ambiguous
//   layout                - vue3's list/cards toggle is not URL-backed
//   subgroupBy            - vue3's kanban groups on one dimension, with no sub-grouping
//   minimumItems.<status> - per-column "show N" cutoffs; vue3 has no equivalent control

// vue2 writes booleans as 'true'/'false'; '1' is accepted too so a hand-edited URL still reads.
function isOn (raw) {
  return raw === 'true' || raw === '1'
}

// Last occurrence of a repeated scalar wins. Returns undefined when the key is absent.
function lastOf (params, key) {
  const all = params.getAll(key)
  return all.length ? all[all.length - 1] : undefined
}

// As above, but restricted to values the rewrite understands. vue2 lets a scalar param repeat and
// keeps the whole list (its Organization preset ships `sort=name&sort=datePutInProduction`, and
// vue2's multi-key sort reads both); vue3 has a single field, so we take the last value it can
// express. When none qualifies the result is '' — "no grouping" / "order by project number", which
// is also what vue2 does with a value it doesn't recognize.
function lastValid (params, key, vocabulary) {
  const all = params.getAll(key).filter(v => vocabulary.includes(v))
  return all.length ? all[all.length - 1] : ''
}

/**
 * Translate a vue2 dashboard query string into the vue3 spelling of the same filter state.
 * `view` is the resolved view name (already validated), or '' for none.
 */
function translateDashboardQuery (view, search) {
  const from = new URLSearchParams(search)
  const out = new URLSearchParams()

  // Key order mirrors vue3's own `toQuery` so the mirrored URL reads like one it produced.
  if (view && view !== DEFAULT_DASHBOARD_VIEW) out.append('view', view)

  // Statuses. The one filter whose ABSENCE differs: vue2 treats an empty status list as "every
  // status", vue3 treats an absent `statuses` as its four-status default (Quoted/InProduction/
  // Completed/New) and only an EXPLICITLY EMPTY `statuses=` as "all". So the key is always
  // written — dropping it would silently narrow the board the user was looking at.
  out.append('statuses', from.getAll('status').join(','))

  Object.keys(CSV_PARAMS).forEach(key => {
    const values = from.getAll(key).filter(v => v !== '')
    if (values.length) out.append(CSV_PARAMS[key], values.join(','))
  })

  Object.keys(FLAG_PARAMS).forEach(key => {
    if (isOn(lastOf(from, key))) out.append(FLAG_PARAMS[key], '1')
  })

  if (lastOf(from, 'direction') === 'column') out.append('direction', 'column')

  // groupBy/sort: same vocabulary, different defaults — vue2 defaults to '' (no grouping / order
  // by project number), vue3 to 'status' / 'datePutInProduction'. An absent key therefore has to
  // be written out as an explicit empty value, or the rewrite would group and sort a board the
  // user had deliberately left ungrouped.
  const groupBy = lastValid(from, 'groupBy', VUE3_GROUP_BY)
  if (groupBy !== 'status') out.append('groupBy', groupBy)
  const sort = lastValid(from, 'sort', VUE3_SORT)
  if (sort !== 'datePutInProduction') out.append('sort', sort)

  // hasItems defaults to true on both sides; vue2 writes 'false', vue3 writes '0'.
  const hasItems = lastOf(from, 'hasItems')
  if (hasItems !== undefined && !isOn(hasItems)) out.append('hasItems', '0')

  // Commas back to their literal form: they are legal in a query value (RFC 3986 sub-delim) and
  // vue3's own router writes its csv params unencoded, so this keeps the mirrored URL identical to
  // one the rewrite would have produced rather than a %2C-riddled lookalike.
  return out.toString().replace(/%2C/g, ',')
}

/**
 * Translate a Vue 2 `$route.fullPath` into the equivalent URL on the Vue 3 app.
 * Returns the input unchanged for every route the two apps spell the same way.
 */
export function toNewAppPath (fullPath) {
  const input = String(fullPath || '')

  const hashAt = input.indexOf('#')
  const hash = hashAt === -1 ? '' : input.slice(hashAt)
  const pathAndSearch = hashAt === -1 ? input : input.slice(0, hashAt)

  const searchAt = pathAndSearch.indexOf('?')
  const path = searchAt === -1 ? pathAndSearch : pathAndSearch.slice(0, searchAt)
  const search = searchAt === -1 ? '' : pathAndSearch.slice(searchAt + 1)

  const dashboard = /^\/dashboard(?:\/([^/]+))?\/?$/.exec(path)
  if (!dashboard) return input

  // The three view names are plain words, so the raw segment is compared as-is; anything encoded
  // can only be a segment we'd drop anyway.
  const view = DASHBOARD_VIEWS.includes(dashboard[1]) ? dashboard[1] : ''

  // A bare `/dashboard` carries no filter state — vue2 is still about to redirect it to the user's
  // default preset — so it is mirrored bare and vue3 applies ITS defaults. Translating "vue2
  // defaults" literally would instead land the user on an ungrouped, unsorted, all-status board.
  if (search === '') {
    const bareView = view && view !== DEFAULT_DASHBOARD_VIEW ? `?view=${view}` : ''
    return `/dashboard${bareView}${hash}`
  }

  const query = translateDashboardQuery(view, search)
  return `/dashboard${query ? `?${query}` : ''}${hash}`
}

export default toNewAppPath
